import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, Mail, KeyRound } from 'lucide-react';
import { Button } from './ui/button';
import { isNativePlatform } from '../lib/native';
import { isAppleSignInSupported } from '../lib/nativeAuth';
import { isWebAppleEnabled, isWebGoogleEnabled } from '../lib/webIdentity';
import { fetchSignInMethods, linkApple, linkGoogle } from '../lib/linkIdentity';
import AddEmailLoginDialog from './AddEmailLoginDialog';
import GoogleWebButton from './GoogleWebButton';
import { useAuth } from '../context/AuthContext';

const CANCELLED = /canceled|cancel|1001|12501|popup_closed/i;

const Row = ({ icon, title, detail, connected, testId, children }) => (
  <div className="flex items-center justify-between gap-4 py-4 border-b border-white/5 last:border-0" data-testid={testId}>
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-9 h-9 rounded-full bg-[#8BAA9E]/10 flex items-center justify-center text-[#8BAA9E] shrink-0">{icon}</div>
      <div className="min-w-0">
        <p className="text-[#DCD3CA] font-sans">{title}</p>
        {detail && <p className="text-xs text-[#7A7670] truncate" data-testid={`${testId}-detail`}>{detail}</p>}
      </div>
    </div>
    {connected ? (
      <span className="flex items-center gap-1 text-sm text-[#8BAA9E] shrink-0" data-testid={`${testId}-connected`}>
        <Check className="w-4 h-4" /> Connected
      </span>
    ) : children}
  </div>
);

const ConnectBtn = ({ onClick, busy, testId, label = 'Connect' }) => (
  <Button size="sm" variant="outline" onClick={onClick} disabled={busy} className="rounded-full border-[#8BAA9E]/40 text-[#DCD3CA] hover:bg-[#8BAA9E]/10 shrink-0" data-testid={testId}>
    {busy ? '…' : label}
  </Button>
);

const Unavailable = ({ testId }) => <span className="text-xs text-[#7A7670] shrink-0" data-testid={testId}>In the iPhone app</span>;

export const SignInMethodsSection = () => {
  const [methods, setMethods] = useState(null);
  const [busy, setBusy] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const { checkAuth } = useAuth();

  const load = useCallback(() => fetchSignInMethods().then(setMethods).catch(() => setMethods(null)), []);
  useEffect(() => { load(); }, [load]);

  const connect = async (provider, fn) => {
    setBusy(provider);
    try {
      const r = await fn();
      toast.success(r?.already_linked ? 'Already connected.' : `${provider === 'apple' ? 'Apple' : 'Google'} connected — you can now sign in with it.`);
      await load();
    } catch (e) {
      if (!CANCELLED.test(e?.message || '') && !CANCELLED.test(e?.error || '')) toast.error(e?.message || 'Could not connect.');
    } finally {
      setBusy(null);
    }
  };

  if (!methods) return null;
  const relay = methods.email_is_private_relay;
  const canApple = isAppleSignInSupported() || isWebAppleEnabled();
  const nativeGoogle = isNativePlatform();

  return (
    <div className="bg-[#252528] border border-white/5 rounded-[24px] p-6" data-testid="sign-in-methods-section">
      <h2 className="font-serif text-xl text-[#DCD3CA] mb-1">Sign-in methods</h2>
      <p className="text-sm text-[#7A7670] mb-2">Every method opens this same account, with all your goals and Premium.</p>

      <Row icon={<Mail className="w-4 h-4" />} title="Email & password" testId="method-email" connected={methods.password}
        detail={methods.password ? methods.email : (relay ? 'Email hidden by Apple' : 'Optional')}>
        <ConnectBtn label="Add" onClick={() => setEmailOpen(true)} testId="add-email-login-btn" />
      </Row>
      <Row icon={<KeyRound className="w-4 h-4" />} title="Apple" testId="method-apple" connected={methods.apple}
        detail={methods.apple && relay ? 'Hide My Email' : null}>
        {canApple ? <ConnectBtn busy={busy === 'apple'} onClick={() => connect('apple', linkApple)} testId="connect-apple-btn" /> : <Unavailable testId="connect-apple-unavailable" />}
      </Row>
      <Row icon={<KeyRound className="w-4 h-4" />} title="Google" testId="method-google" connected={methods.google} detail={methods.google_email}>
        {nativeGoogle && <ConnectBtn busy={busy === 'google'} onClick={() => connect('google', () => linkGoogle())} testId="connect-google-btn" />}
        {!nativeGoogle && isWebGoogleEnabled() && (
          <div className="w-[200px]"><GoogleWebButton text="signin" testId="connect-google-web-btn" onCredential={(cred) => connect('google', () => linkGoogle(cred))} /></div>
        )}
        {!nativeGoogle && !isWebGoogleEnabled() && <Unavailable testId="connect-google-unavailable" />}
      </Row>

      <AddEmailLoginDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        defaultEmail={relay ? '' : methods.email || ''}
        emailLocked={!!methods.email && !relay}
        onDone={() => { load(); checkAuth(); }}
      />
    </div>
  );
};

export default SignInMethodsSection;
